import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw, Trash2, Users, Check, X, Loader2, Heart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_KEY = "mm_admin_pw";

const StatCard = ({ label, value, accent, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-[#EADFE3] bg-white p-4 text-center shadow-sm">
    <p className="text-3xl font-semibold" style={{ color: accent }}>{value}</p>
    <p className="mt-1 text-xs tracking-wide text-[#8C8084]">{label}</p>
  </div>
);

const Admin = () => {
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loginErr, setLoginErr] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [data, setData] = useState({ stats: null, items: [] });
  const [loading, setLoading] = useState(false);
  const [fetchErr, setFetchErr] = useState("");

  const fetchData = useCallback(async (password) => {
    setLoading(true);
    setFetchErr("");
    try {
      const res = await axios.get(`${API}/admin/rsvps`, {
        headers: { "X-Admin-Password": password },
      });
      setData(res.data);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        setFetchErr("401");
        setAuthed(false);
        sessionStorage.removeItem(STORE_KEY);
      } else {
        setFetchErr("დაფიქსირდა შეცდომა მონაცემების ჩატვირთვისას.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(STORE_KEY);
    if (saved) {
      setPw(saved);
      fetchData(saved);
    }
  }, [fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!pw.trim() || loggingIn) return;
    setLoggingIn(true);
    setLoginErr("");
    try {
      await axios.post(`${API}/admin/login`, { password: pw });
      sessionStorage.setItem(STORE_KEY, pw);
      await fetchData(pw);
    } catch (err) {
      setLoginErr("არასწორი პაროლი. სცადეთ თავიდან.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORE_KEY);
    setAuthed(false);
    setPw("");
    setData({ stats: null, items: [] });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("წავშალო ეს პასუხი?")) return;
    try {
      await axios.delete(`${API}/admin/rsvps/${id}`, {
        headers: { "X-Admin-Password": pw },
      });
      fetchData(pw);
    } catch (err) {
      alert("წაშლა ვერ მოხერხდა.");
    }
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("ka-GE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  // ===== Login screen =====
  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDF9FB] px-5">
        <div className="w-full max-w-sm rounded-2xl border border-[#EADFE3] bg-white p-8 shadow-[0_30px_70px_-34px_rgba(140,70,95,0.3)]">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#D49CAE] to-[#B0607A] text-white">
            <Lock width={22} height={22} />
          </div>
          <h1 className="text-center text-xl font-semibold text-[#2A2024]">ადმინ პანელი</h1>
          <p className="mt-1 text-center text-sm text-[#8C8084]">მირანდა &amp; მირზა — სტუმრების პასუხები</p>
          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <input
              data-testid="admin-password-input"
              type="password"
              className="input-elegant"
              placeholder="შეიყვანეთ პაროლი"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
            />
            {loginErr && <p data-testid="admin-login-error" className="text-sm text-[#B4455A]">{loginErr}</p>}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loggingIn}
              className="btn-gold flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold uppercase tracking-[0.12em] disabled:opacity-60"
            >
              {loggingIn && <Loader2 className="animate-spin" width={16} height={16} />}
              შესვლა
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ===== Dashboard =====
  const stats = data.stats || {};
  return (
    <div className="min-h-screen bg-[#FDF9FB] px-4 py-8 sm:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Heart className="text-[#B0607A]" width={20} height={20} fill="currentColor" />
            <h1 className="text-xl font-semibold text-[#2A2024]">სტუმრების პასუხები</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={() => fetchData(pw)}
              className="flex items-center gap-2 rounded-full border border-[#E0C4CE] bg-white px-4 py-2 text-xs font-semibold text-[#7E4A5C] transition hover:bg-[#FDF4F7]"
            >
              <RefreshCw width={14} height={14} className={loading ? "animate-spin" : ""} /> განახლება
            </button>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-full border border-[#E0C4CE] bg-white px-4 py-2 text-xs font-semibold text-[#7E4A5C] transition hover:bg-[#FDF4F7]"
            >
              <LogOut width={14} height={14} /> გასვლა
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard testid="stat-total" label="სულ პასუხი" value={stats.total_responses ?? 0} accent="#2A2024" />
          <StatCard testid="stat-coming" label="მოდის" value={stats.coming_responses ?? 0} accent="#3F9142" />
          <StatCard testid="stat-not-coming" label="ვერ მოდის" value={stats.not_coming_responses ?? 0} accent="#B4455A" />
          <StatCard testid="stat-guests" label="სულ სტუმარი" value={stats.total_guests ?? 0} accent="#B0607A" />
        </div>

        {fetchErr && fetchErr !== "401" && (
          <p className="mt-5 text-center text-sm text-[#B4455A]">{fetchErr}</p>
        )}

        <div className="mt-6 overflow-hidden rounded-2xl border border-[#EADFE3] bg-white shadow-sm">
          {loading && data.items.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-16 text-[#8C8084]">
              <Loader2 className="animate-spin" width={18} height={18} /> იტვირთება...
            </div>
          ) : data.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-[#8C8084]">
              <Users width={28} height={28} />
              <p>ჯერ არავის უპასუხია</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table data-testid="admin-rsvp-table" className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-[#EFE4E8] text-xs uppercase tracking-wider text-[#A89AA0]">
                    <th className="px-4 py-3 font-semibold">სახელი</th>
                    <th className="px-4 py-3 font-semibold">სტატუსი</th>
                    <th className="px-4 py-3 text-center font-semibold">სტუმარი</th>
                    <th className="px-4 py-3 font-semibold">თარიღი</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.id} data-testid="admin-rsvp-row" className="border-b border-[#F3ECEF] last:border-0">
                      <td className="px-4 py-3 font-medium text-[#2A2024]">{r.name}</td>
                      <td className="px-4 py-3">
                        {r.attendance === "yes" ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#E9F5EA] px-2.5 py-1 text-xs font-semibold text-[#3F9142]">
                            <Check width={12} height={12} /> მოდის
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#FBEAEE] px-2.5 py-1 text-xs font-semibold text-[#B4455A]">
                            <X width={12} height={12} /> ვერ მოდის
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-[#57504F]">{r.guests}</td>
                      <td className="px-4 py-3 text-[#8C8084]">{formatDate(r.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="text-[#C09AA6] transition hover:text-[#B4455A]"
                          aria-label="წაშლა"
                        >
                          <Trash2 width={16} height={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
